import { useQuery } from '@tanstack/react-query';
import { buildQueryString } from '@/lib/api/client';
import { recipientsFetch } from '@/lib/recipients/api';
import { auditKeys } from '@/hooks/api/query-keys';

// ============ Types ============

export interface AuditLog {
  id: string;
  entityType: string;
  entityId: string;
  action: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  createdBy: string;
  createdByName?: string;
}

// ============ Query Hooks ============

export function useAuditLogs(filter?: Record<string, unknown>) {
  return useQuery({
    queryKey: auditKeys.list(filter),
    queryFn: async () => {
      const qs = filter ? buildQueryString(filter) : '';
      const result = await recipientsFetch<
        { data: AuditLog[]; total: number } | AuditLog[]
      >(`audit${qs}`);
      if (Array.isArray(result)) {
        return { data: result, total: result.length };
      }
      return result;
    },
  });
}

export function useAuditMetrics() {
  return useQuery({
    queryKey: auditKeys.metrics(),
    queryFn: () =>
      recipientsFetch<{
        totalActions: number;
        actionsByType: Record<string, number>;
        recentActivity: { date: string; count: number }[];
        // Server-computed "This Week" count + exact cutoff. The page
        // displays `thisWeekCount` in the card and passes
        // `thisWeekStartIso` back to the list endpoint as `startDate`
        // when the user clicks the card, so the card number and the
        // filtered table footer always agree. See Bug B fix in
        // src/modules/recipients/audit/audit.service.ts:getMetrics.
        thisWeekCount?: number;
        thisWeekStartIso?: string;
      }>('audit/metrics'),
  });
}
